import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRightIcon,
  PencilSimpleIcon,
  CheckIcon,
  XIcon,
  WarningIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import CopyButton from "../../components/CopyButton";
import {
  DangerButton,
  ErrorBanner,
  INPUT_CLS,
  PrimaryButton,
  SecondaryButton,
  TextArea,
} from "../../components/crm/Form";
import RMAStatusBadge from "../../components/rma/RMAStatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiError } from "../../lib/api";
import {
  getRMARecord,
  isoDate,
  linkRMAWorkOrder,
  RMA_COMPONENT_LABEL,
  RMA_STATUS_ACTIONS,
  RMA_STATUS_LABEL,
  RMA_STATUS_TINT,
  RMA_VENDOR_LABEL,
  relativeTime,
  shortId,
  transitionRMAStatus,
  updateRMA,
  woDisplay,
} from "../../lib/rma";

export default function RMADetail() {
  const { rmaId } = useParams();
  const navigate = useNavigate();
  const [rma, setRma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getRMARecord(rmaId);
      setRma(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setErr("RMA not found.");
      } else {
        setErr(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [rmaId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Layout><DetailSkeleton /></Layout>;
  if (err || !rma)
    return (
      <Layout>
        <ErrorBanner message={err} />
        <button
          onClick={() => navigate("/rma-records")}
          className="text-sm text-slate-700 hover:text-slate-900 font-semibold"
        >
          ← Back to RMA list
        </button>
      </Layout>
    );

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title={
            <span className="inline-flex items-center gap-2 font-data text-2xl tracking-wide">
              {rma.rma_number}
              <CopyButton
                value={rma.rma_number}
                label="RMA Number"
                testId="rma-copy-number"
                size={14}
              />
            </span>
          }
          subtitle={
            <span className="flex items-center gap-2 flex-wrap">
              <RMAStatusBadge status={rma.status} size="md" />
              <span className="text-xs text-slate-600">
                {RMA_VENDOR_LABEL[rma.vendor]} ·{" "}
                {RMA_COMPONENT_LABEL[rma.component_type]}
              </span>
            </span>
          }
        />

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left column ----------------------------------------------- */}
          <div className="space-y-4">
            <HeaderCard rma={rma} />
            <ComponentDetailsCard rma={rma} onUpdate={load} />
            <VendorCaseCard rma={rma} onUpdate={load} />
            <StatusHistoryCard history={rma.status_history || []} />
            <LinkedWorkOrdersCard rma={rma} onUpdate={load} />
          </div>

          {/* Right sidebar -------------------------------------------- */}
          <aside className="space-y-4">
            <StatusActionCard rma={rma} onUpdate={load} />
            <QuickInfoCard rma={rma} />
            <DangerZoneCard rma={rma} onUpdate={load} />
          </aside>
        </div>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function HeaderCard({ rma }) {
  const wo = rma.diagnostic_report_summary?.work_order_id;
  const finalized = rma.diagnostic_report_summary?.finalized_at;
  return (
    <section
      data-testid="rma-header-card"
      className="bg-white border border-slate-200 rounded-md p-5 space-y-2"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        Customer
      </div>
      {rma.customer_id ? (
        <Link
          to={`/customers/${rma.customer_id}`}
          className="text-base font-semibold text-slate-900 hover:underline"
          data-testid="rma-header-customer-link"
        >
          {rma.customer_name || "(unknown)"}
        </Link>
      ) : (
        <div className="text-base font-semibold text-slate-900">
          {rma.customer_name || "(unknown)"}
        </div>
      )}
      {rma.property_address && (
        <div className="text-sm text-slate-700">{rma.property_address}</div>
      )}
      <div className="pt-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        Linked diagnostic report
      </div>
      {wo ? (
        <Link
          to={`/work-orders/${wo}/report`}
          className="text-sm font-semibold text-slate-900 hover:underline"
          data-testid="rma-header-report-link"
        >
          {woDisplay(wo)}
          {finalized ? ` · Finalized ${relativeTime(finalized)}` : ""}
        </Link>
      ) : (
        <div className="text-sm text-slate-700">—</div>
      )}
    </section>
  );
}

function ComponentDetailsCard({ rma, onUpdate }) {
  const fields = [
    { key: "component_serial", label: "Component serial", type: "text" },
    { key: "component_model", label: "Component model", type: "text" },
    { key: "warranty_expiry", label: "Warranty expiry", type: "date" },
    { key: "replacement_serial", label: "Replacement serial", type: "text" },
    { key: "replacement_model", label: "Replacement model", type: "text" },
    { key: "tracking_number", label: "Tracking number", type: "text" },
    { key: "carrier", label: "Carrier", type: "text" },
    { key: "estimated_arrival", label: "Estimated arrival", type: "date" },
  ];
  return (
    <section
      data-testid="rma-component-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
        Component & shipment details
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {fields.map((f) => (
          <InlineEditField
            key={f.key}
            rmaId={rma.id}
            field={f.key}
            label={f.label}
            type={f.type}
            value={rma[f.key]}
            onSaved={onUpdate}
          />
        ))}
      </dl>
    </section>
  );
}

function VendorCaseCard({ rma, onUpdate }) {
  return (
    <section
      data-testid="rma-vendor-case-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
        Vendor case
      </div>
      <dl className="space-y-3">
        <InlineEditField
          rmaId={rma.id}
          field="vendor_case_number"
          label="Vendor case number"
          type="text"
          value={rma.vendor_case_number}
          onSaved={onUpdate}
        />
        <InlineEditField
          rmaId={rma.id}
          field="internal_notes"
          label="Internal notes"
          type="textarea"
          value={rma.internal_notes}
          maxLength={2000}
          onSaved={onUpdate}
        />
      </dl>
    </section>
  );
}

function StatusHistoryCard({ history }) {
  if (!history.length) {
    return (
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
          Status history
        </div>
        <div className="text-xs text-slate-500">No transitions yet.</div>
      </section>
    );
  }
  const sorted = [...history].sort((a, b) =>
    (a.changed_at || "").localeCompare(b.changed_at || "")
  );
  return (
    <section
      data-testid="rma-history-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
        Status history
      </div>
      <ul className="space-y-3">
        {sorted.map((e, idx) => (
          <li
            key={`${e.changed_at}-${idx}`}
            className="flex items-start gap-3 text-xs"
          >
            <div className="flex items-center gap-1 flex-none">
              {e.from_status ? (
                <span
                  className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    RMA_STATUS_TINT[e.from_status] || ""
                  }`}
                >
                  {RMA_STATUS_LABEL[e.from_status] || e.from_status}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
              <ArrowRightIcon size={12} className="text-slate-400" />
              <span
                className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                  RMA_STATUS_TINT[e.to_status] || ""
                }`}
              >
                {RMA_STATUS_LABEL[e.to_status] || e.to_status}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-slate-700">
                <span title={e.changed_at}>{relativeTime(e.changed_at)}</span>
                {e.changed_by_name && (
                  <span className="text-slate-500"> · {e.changed_by_name}</span>
                )}
              </div>
              {e.note && (
                <div className="italic text-slate-600 mt-0.5">
                  &ldquo;{e.note}&rdquo;
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkedWorkOrdersCard({ rma, onUpdate }) {
  const [showLink, setShowLink] = useState(false);
  const [woInput, setWoInput] = useState("");
  const [busy, setBusy] = useState(false);

  const canLink =
    !rma.replacement_work_order_id &&
    (rma.status === "part_received" || rma.status === "install_scheduled");

  const submit = async () => {
    if (!woInput.trim()) return;
    setBusy(true);
    try {
      await linkRMAWorkOrder(rma.id, woInput.trim());
      toast.success("Replacement WO linked");
      setShowLink(false);
      setWoInput("");
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="rma-wo-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
        Linked work orders
      </div>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            Original work order
          </dt>
          <dd className="mt-0.5">
            <Link
              to={`/work-orders/${rma.work_order_id}`}
              className="text-slate-900 hover:underline font-data text-xs"
            >
              {woDisplay(rma.work_order_id)}
            </Link>
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            Replacement install work order
          </dt>
          <dd className="mt-0.5">
            {rma.replacement_work_order_id ? (
              <Link
                to={`/work-orders/${rma.replacement_work_order_id}`}
                className="text-slate-900 hover:underline font-data text-xs"
                data-testid="rma-wo-replacement-link"
              >
                {woDisplay(rma.replacement_work_order_id)}
              </Link>
            ) : (
              <span className="text-slate-500 text-xs">Not yet linked</span>
            )}
          </dd>
        </div>
      </dl>

      {canLink && !showLink && (
        <div className="mt-4">
          <SecondaryButton
            type="button"
            onClick={() => setShowLink(true)}
            data-testid="rma-wo-link-btn"
          >
            Link replacement WO
          </SecondaryButton>
        </div>
      )}

      {showLink && (
        <div className="mt-4 space-y-2 rounded-md border border-amber-200 bg-amber-50/50 p-3">
          <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold">
            Work order ID
          </label>
          <input
            type="text"
            value={woInput}
            onChange={(e) => setWoInput(e.target.value)}
            placeholder="Full UUID of the install WO"
            data-testid="rma-wo-link-input"
            className={INPUT_CLS}
          />
          <div className="flex justify-end gap-2 pt-1">
            <SecondaryButton
              type="button"
              onClick={() => {
                setShowLink(false);
                setWoInput("");
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={submit}
              disabled={busy || !woInput.trim()}
              data-testid="rma-wo-link-confirm"
            >
              {busy ? "Linking…" : "Confirm"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sidebar cards
// ---------------------------------------------------------------------------

function StatusActionCard({ rma, onUpdate }) {
  const actions = RMA_STATUS_ACTIONS[rma.status] || [];
  const [pending, setPending] = useState(null);  // {to, kind}
  const [busy, setBusy] = useState(false);
  const [denialReason, setDenialReason] = useState("");
  const [appealNote, setAppealNote] = useState("");
  const [shipForm, setShipForm] = useState({
    tracking_number: "",
    carrier: "",
    estimated_arrival: "",
  });

  const reset = () => {
    setPending(null);
    setDenialReason("");
    setAppealNote("");
    setShipForm({ tracking_number: "", carrier: "", estimated_arrival: "" });
  };

  const fire = async (body) => {
    setBusy(true);
    try {
      await transitionRMAStatus(rma.id, body);
      toast.success(`Status updated to ${RMA_STATUS_LABEL[body.to_status]}`);
      reset();
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startAction = (action) => {
    setPending(action);
  };

  const cancelAction = () => reset();

  const confirmSimple = () => fire({ to_status: pending.to });

  const confirmDenial = () => {
    if (denialReason.trim().length < 5) {
      toast.error("Denial reason must be at least 5 characters.");
      return;
    }
    fire({ to_status: "denied", denial_reason: denialReason.trim() });
  };

  const confirmAppeal = () => {
    const body = { to_status: "appealing" };
    if (appealNote.trim()) body.note = appealNote.trim();
    fire(body);
  };

  const confirmShip = () => {
    const body = { to_status: "part_shipped" };
    if (shipForm.tracking_number.trim())
      body.tracking_number = shipForm.tracking_number.trim();
    if (shipForm.carrier.trim()) body.carrier = shipForm.carrier.trim();
    if (shipForm.estimated_arrival) {
      body.estimated_arrival = `${shipForm.estimated_arrival}T00:00:00+00:00`;
    }
    fire(body);
  };

  return (
    <section
      data-testid="rma-action-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
        Current status
      </div>
      <div className="mb-4">
        <RMAStatusBadge status={rma.status} size="lg" />
      </div>

      {actions.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="rma-action-complete">
          Case complete
        </div>
      )}

      {actions.length > 0 && !pending && (
        <div className="space-y-2">
          {actions.map((a) => {
            const Btn = a.primary ? PrimaryButton : SecondaryButton;
            return (
              <Btn
                key={a.to}
                type="button"
                onClick={() => startAction(a)}
                className="w-full"
                data-testid={`rma-action-${a.to}`}
              >
                {a.label}
              </Btn>
            );
          })}
        </div>
      )}

      {/* Confirm step / inline forms */}
      {pending && pending.kind === "simple" && (
        <ConfirmRow
          message={
            <>
              Transition to{" "}
              <strong>{RMA_STATUS_LABEL[pending.to]}</strong>?
            </>
          }
          onCancel={cancelAction}
          onConfirm={confirmSimple}
          busy={busy}
        />
      )}

      {pending && pending.kind === "form_denial" && (
        <div className="mt-3 space-y-2">
          <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold">
            Denial reason (required, 5–1000 chars)
          </label>
          <TextArea
            value={denialReason}
            onChange={setDenialReason}
            rows={3}
            maxLength={1000}
            data-testid="rma-action-denial-reason"
          />
          <div className="text-[11px] text-slate-500">
            {denialReason.length}/1000
          </div>
          <ConfirmRow
            confirmLabel="Confirm denial"
            onCancel={cancelAction}
            onConfirm={confirmDenial}
            busy={busy}
            confirmDisabled={denialReason.trim().length < 5}
          />
        </div>
      )}

      {pending && pending.kind === "form_appeal" && (
        <div className="mt-3 space-y-2">
          <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold">
            Appeal note (optional, max 500 chars)
          </label>
          <TextArea
            value={appealNote}
            onChange={setAppealNote}
            rows={3}
            maxLength={500}
            data-testid="rma-action-appeal-note"
          />
          <ConfirmRow
            confirmLabel="Confirm appeal"
            onCancel={cancelAction}
            onConfirm={confirmAppeal}
            busy={busy}
          />
        </div>
      )}

      {pending && pending.kind === "form_part_shipped" && (
        <div className="mt-3 space-y-3">
          <ShipFieldRow
            label="Tracking number"
            value={shipForm.tracking_number}
            onChange={(v) => setShipForm((f) => ({ ...f, tracking_number: v }))}
            testid="rma-action-tracking"
          />
          <ShipFieldRow
            label="Carrier"
            value={shipForm.carrier}
            onChange={(v) => setShipForm((f) => ({ ...f, carrier: v }))}
            testid="rma-action-carrier"
          />
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1">
              Estimated arrival
            </label>
            <input
              type="date"
              value={shipForm.estimated_arrival}
              onChange={(e) =>
                setShipForm((f) => ({
                  ...f,
                  estimated_arrival: e.target.value,
                }))
              }
              data-testid="rma-action-eta"
              className={INPUT_CLS}
            />
          </div>
          <ConfirmRow
            confirmLabel="Confirm shipment"
            onCancel={cancelAction}
            onConfirm={confirmShip}
            busy={busy}
          />
        </div>
      )}
    </section>
  );
}

function ShipFieldRow({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={INPUT_CLS}
      />
    </div>
  );
}

function ConfirmRow({
  message,
  confirmLabel = "Confirm",
  onCancel,
  onConfirm,
  busy,
  confirmDisabled,
}) {
  return (
    <div className="space-y-2">
      {message && (
        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          {message}
        </div>
      )}
      <div className="flex gap-2">
        <SecondaryButton
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          className="flex-1"
          data-testid="rma-action-confirm"
        >
          {busy ? "Working…" : confirmLabel}
        </PrimaryButton>
      </div>
    </div>
  );
}

function QuickInfoCard({ rma }) {
  return (
    <section
      data-testid="rma-quick-info"
      className="bg-white border border-slate-200 rounded-md p-4 space-y-2 text-xs"
    >
      <Row
        label="Created"
        value={
          <>
            <span title={rma.created_at}>{relativeTime(rma.created_at)}</span>
            {rma.created_by_name && (
              <span className="text-slate-500"> · by {rma.created_by_name}</span>
            )}
          </>
        }
      />
      <Row
        label="Last updated"
        value={
          <span title={rma.updated_at}>{relativeTime(rma.updated_at)}</span>
        }
      />
      <Row
        label="Status events"
        value={String((rma.status_history || []).length)}
      />
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}

function DangerZoneCard({ rma, onUpdate }) {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "owner") return null;
  // Show Danger Zone for every status EXCEPT the two terminal states.
  if (rma.status === "closed" || rma.status === "voided") return null;

  const submit = async () => {
    setBusy(true);
    try {
      await transitionRMAStatus(rma.id, { to_status: "voided" });
      toast.success("RMA voided");
      setShowForm(false);
      setConfirmInput("");
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="rma-danger-zone"
      className="bg-red-50/40 border border-red-200 rounded-md p-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-red-800 font-bold mb-2">
        <WarningIcon size={14} />
        Danger zone
      </div>
      {!showForm && (
        <DangerButton
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full"
          data-testid="rma-void-btn"
        >
          Void this RMA
        </DangerButton>
      )}
      {showForm && (
        <div className="space-y-2">
          <p className="text-xs text-red-900">
            Type <code className="font-data">{rma.rma_number}</code> to confirm.
          </p>
          <input
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            data-testid="rma-void-confirm-input"
            className={INPUT_CLS}
          />
          <div className="flex gap-2">
            <SecondaryButton
              type="button"
              onClick={() => {
                setShowForm(false);
                setConfirmInput("");
              }}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              type="button"
              onClick={submit}
              disabled={busy || confirmInput.trim() !== rma.rma_number}
              className="flex-1"
              data-testid="rma-void-confirm"
            >
              {busy ? "Voiding…" : "Void"}
            </DangerButton>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// InlineEditField — pencil-toggled inline editor used on the left column
// ---------------------------------------------------------------------------

function InlineEditField({
  rmaId,
  field,
  label,
  type = "text",
  value,
  maxLength,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(_initialDraft(value, type));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(_initialDraft(value, type));
  }, [value, type, editing]);

  const startEdit = () => {
    setDraft(_initialDraft(value, type));
    setEditing(true);
  };
  const cancel = () => setEditing(false);

  const save = async () => {
    if (busy) return;
    const payload = {};
    payload[field] = _serialize(draft, type);
    setBusy(true);
    try {
      await updateRMA(rmaId, payload);
      toast.success("Saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && type !== "textarea") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (!editing) {
    return (
      <div data-testid={`rma-field-${field}`}>
        <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          {label}
        </dt>
        <dd
          className="mt-0.5 flex items-center justify-between gap-2 group cursor-pointer"
          onClick={startEdit}
        >
          <span className="text-sm text-slate-900 break-all">
            {_displayValue(value, type)}
          </span>
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 transition rounded p-1 text-slate-500 hover:text-slate-900"
            aria-label={`Edit ${label}`}
            data-testid={`rma-field-edit-${field}`}
            onClick={(e) => {
              e.stopPropagation();
              startEdit();
            }}
          >
            <PencilSimpleIcon size={14} />
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div data-testid={`rma-field-edit-${field}`}>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </dt>
      <dd className="mt-0.5 flex items-start gap-1.5">
        {type === "textarea" ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={save}
            rows={3}
            maxLength={maxLength}
            autoFocus
            className={INPUT_CLS}
          />
        ) : (
          <input
            type={type === "date" ? "date" : "text"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={save}
            maxLength={maxLength}
            autoFocus
            className={INPUT_CLS}
          />
        )}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}  // keep input focused
          onClick={save}
          disabled={busy}
          className="rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 p-1.5 text-emerald-700 disabled:opacity-50"
          aria-label="Save"
        >
          <CheckIcon size={14} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          className="rounded-md border border-slate-300 bg-white hover:bg-slate-50 p-1.5 text-slate-700"
          aria-label="Cancel"
        >
          <XIcon size={14} />
        </button>
      </dd>
    </div>
  );
}

function _initialDraft(value, type) {
  if (value === null || value === undefined) return "";
  if (type === "date") {
    // Strip time component for HTML <input type=date>
    return String(value).slice(0, 10);
  }
  return String(value);
}

function _serialize(draft, type) {
  // Per Point B (override): empty input → null on the wire.
  const trimmed = (draft || "").trim();
  if (!trimmed) return null;
  if (type === "date") {
    return `${trimmed}T00:00:00+00:00`;
  }
  return trimmed;
}

function _displayValue(value, type) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-slate-400">—</span>;
  }
  if (type === "date") return isoDate(value);
  return value;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-64 bg-slate-100 rounded" />
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 bg-slate-100 rounded-md border border-slate-200"
            />
          ))}
        </div>
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 bg-slate-100 rounded-md border border-slate-200"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
